import React, { useState, useEffect } from 'react';
import { AlertTriangle, Package, Clock, TrendingDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StockAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStockAlerts();
  }, []);

  const fetchStockAlerts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/stock/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching stock alerts:', error);
      toast.error('Failed to load stock alerts');
    } finally {
      setLoading(false);
    }
  };

  // Restock functionality removed - buttons no longer available

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCriticalityLevel = (currentStock, threshold) => {
    if (currentStock === 0) return { level: 'critical', color: 'red', label: 'Critical' };
    if (currentStock === 1) return { level: 'urgent', color: 'orange', label: 'Urgent' };
    return { level: 'warning', color: 'amber', label: 'Warning' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="stock-alerts">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Stock Alerts</h1>
        <p className="text-gray-600 mt-2">Monitor and respond to low stock notifications</p>
      </div>

      {/* Alert Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Alerts</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{alerts.length}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Critical (0 stock)</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {alerts.filter(alert => alert.current_stock === 0).length}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Urgent (1 stock)</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {alerts.filter(alert => alert.current_stock === 1).length}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Warning (2+ stock)</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {alerts.filter(alert => alert.current_stock >= 2).length}
              </p>
            </div>
            <div className="bg-amber-100 p-3 rounded-lg">
              <Package className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      {alerts.length > 0 ? (
        <div className="space-y-4">
          {alerts.map((alert) => {
            const criticality = getCriticalityLevel(alert.current_stock, alert.threshold);
            
            return (
              <div 
                key={alert.id} 
                className={`bg-white rounded-xl shadow-sm border-l-4 border-${criticality.color}-500 p-6`}
                data-testid={`alert-${alert.id}`}
              >
                <div className="w-full">
                  <div className="w-full">
                    <div className="flex items-center space-x-3 mb-3">
                      <AlertTriangle className={`w-5 h-5 text-${criticality.color}-600`} />
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${criticality.color}-100 text-${criticality.color}-800`}>
                        {criticality.label}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(alert.created_at)}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {alert.product_name}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Size:</span>
                        <span className="ml-2 text-gray-900">US {alert.size}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Color:</span>
                        <span className="ml-2 text-gray-900">{alert.color}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Current Stock:</span>
                        <span className={`ml-2 font-bold text-${criticality.color}-600`}>
                          {alert.current_stock}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Threshold:</span>
                        <span className="ml-2 text-gray-900">{alert.threshold}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Stock Alerts</h3>
          <p className="text-gray-500">All products are adequately stocked at the moment.</p>
        </div>
      )}
    </div>
  );
};

export default StockAlerts;